import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import PageWrapper from '../components/PageWrapper';
import Hero from '../sections/Hero';

// Lazy load below-the-fold sections to minimize initial JS download/execution size,
// prevent unused JS execution, and optimize main-thread performance.
const About = React.lazy(() => import('../sections/About'));
const Lighthouse = React.lazy(() => import('../sections/Lighthouse'));
const Projects = React.lazy(() => import('../sections/Projects'));
const Offer = React.lazy(() => import('../sections/Offer'));
const WhyUs = React.lazy(() => import('../sections/WhyUs'));
const ScalingSystem = React.lazy(() => import('../sections/ScalingSystem'));
const Blog = React.lazy(() => import('../sections/Blog'));
const Contact = React.lazy(() => import('../sections/Contact'));

export default function Home() {
  const { language } = useLanguage();

  return (
    <main className="relative bg-black">
      <Helmet>
        <title>
          {language === 'PL' 
            ? 'Agencja Marketingowa Lublin · Strony Internetowe, SEO & Ads' 
            : 'Marketing Agency Lublin · Websites, SEO & Ads'}
        </title>
        <meta name="description" content={language === 'PL' ? 'Dedykowane strony internetowe zoptymalizowane pod SEO · PageSpeed >95% · sklepy 3D & Profesjonalne pozycjonowanie stron · Kampanie Google Ads & Meta Ads' : 'Dedicated websites optimized for SEO · PageSpeed >95% · 3D shops & Professional SEO · Google Ads & Meta Ads campaigns.'} />
        <link rel="canonical" href="https://solidagency.pl/" />
      </Helmet>
      {/* 1. HERO - STICKS TO TOP AND RECEDES (Sync load to maximize LCP) */}
      <Hero />

      {/* 2. PAGE CONTENT WITH LINES AND BACKGROUND */}
      <PageWrapper>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <About />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Lighthouse />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Projects />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Offer showFullOfferButton origin="home" />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <WhyUs 
            title1="Technologia, która "
            title1En="Technology that "
            title2="zarabia."
            title2En="earns."
            subtitle="Własny kod, szybkie strony, kampanie oparte na danych i SEO, które działa długoterminowo. Wszystko połączone ze sobą w jednym miejscu."
            subtitleEn="Custom code, fast sites, data-driven campaigns, and long-term SEO. All in one place, no excuses, and no fluff."
            items={[
              {
                id: 'production',
                pl: 'Prędkość',
                en: 'Speed',
                descPl: 'Strony, które ładują się w ułamku sekundy. To nie estetyka - to bezpośredni wpływ na sprzedaż, pozycje w Google i to czy użytkownik zostaje czy ucieka.',
                descEn: 'Sites that load in a fraction of a second. It is not aesthetics - it\'s a direct impact on sales, Google rankings, and whether the user stays or leaves.'
              },
              {
                id: 'animation',
                pl: 'Kod',
                en: 'Code',
                descPl: 'Żadnych gotowych szablonów i page builderów. Każda strona pisana od zera - szybsza, bezpieczniejsza i bez ograniczeń, które blokują Twój rozwój.',
                descEn: 'No ready-made templates or page builders. Every page written from scratch - faster, safer, and without limitations that block your growth.'
              },
              {
                id: 'web',
                pl: 'Kompleksowość',
                en: 'Complexity',
                descPl: 'Strony, sklepy, SEO, reklamy - wszystko w jednym miejscu i spójnie ze sobą połączone. Nie musisz koordynować pięciu różnych firm.',
                descEn: 'Websites, shops, SEO, ads - everything in one place and consistently connected. You don\'t have to coordinate five different companies.'
              },
              {
                id: 'localization',
                pl: 'Szczerość',
                en: 'Honesty',
                descPl: 'Mówimy wprost co działa, co nie działa i dlaczego. Żadnych skomplikowanych raportów żeby ukryć brak efektów - tylko konkrety.',
                descEn: 'We speak directly about what works, what doesn\'t work, and why. No complicated reports to hide lack of results - just concrete facts.'
              },
              {
                id: 'staff',
                pl: 'Konwersja',
                en: 'Conversion',
                descPl: 'Szybka strona to nie tylko lepsze SEO. To użytkownik który zostaje, przegląda i kupuje zamiast wychodzić po 3 sekundach.',
                descEn: 'A fast website is not just better SEO. It\'s a user who stays, browses, and buys instead of leaving after 3 seconds.'
              }
            ]}
          />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <ScalingSystem />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Blog />
        </Suspense>
        <Suspense fallback={<div className="min-h-[400px]" />}>
          <Contact />
        </Suspense>
      </PageWrapper>
    </main>
  );
}
